import type { SegmentType } from '@/types/timeline';

export interface TimelineSegmentProps {
  /** X position in pixels */
  x: number;
  /** Y position in pixels */
  y: number;
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
  /** Fill color */
  fill: string;
  /** Segment type for identification */
  segmentType: SegmentType;
  /** Whether this is the first segment (rounded left corners) */
  isFirst?: boolean;
  /** Whether this is the last segment (rounded right corners) */
  isLast?: boolean;
  /** Border radius for rounded corners */
  radius?: number;
  /** Mouse enter handler */
  onMouseEnter?: (event: React.MouseEvent<SVGElement>, segmentType: SegmentType) => void;
  /** Mouse leave handler */
  onMouseLeave?: () => void;
}

/**
 * Timeline segment atom - renders a single bar segment in the timeline
 *
 * Supports rounded corners on first/last segments and hover interactions.
 */
export function TimelineSegment({
  x,
  y,
  width,
  height,
  fill,
  segmentType,
  isFirst = false,
  isLast = false,
  radius = 4,
  onMouseEnter,
  onMouseLeave,
}: TimelineSegmentProps) {
  const handleMouseEnter = (event: React.MouseEvent<SVGElement>) => {
    onMouseEnter?.(event, segmentType);
  };

  // Ensure minimum width for visibility
  const effectiveWidth = Math.max(width, 2);

  // Render rounded path for first/last segments, rect for middle segments
  if (isFirst && !isLast) {
    // Rounded left corners only
    const r = radius;
    const d = `M${x + r},${y} H${x + effectiveWidth} V${y + height} H${x + r} A${r},${r} 0 0 1 ${x},${y + height - r} V${y + r} A${r},${r} 0 0 1 ${x + r},${y}`;
    return (
      <path d={d} fill={fill} className="cursor-pointer" onMouseEnter={handleMouseEnter} onMouseLeave={onMouseLeave} />
    );
  }

  if (isLast && !isFirst) {
    // Rounded right corners only
    const r = radius;
    const d = `M${x},${y} H${x + effectiveWidth - r} A${r},${r} 0 0 1 ${x + effectiveWidth},${y + r} V${y + height - r} A${r},${r} 0 0 1 ${x + effectiveWidth - r},${y + height} H${x} V${y}`;
    return (
      <path d={d} fill={fill} className="cursor-pointer" onMouseEnter={handleMouseEnter} onMouseLeave={onMouseLeave} />
    );
  }

  if (isFirst && isLast) {
    // Fully rounded (single segment)
    return (
      <rect
        x={x}
        y={y}
        width={effectiveWidth}
        height={height}
        fill={fill}
        rx={radius}
        className="cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  // Middle segment - no rounded corners
  return (
    <rect
      x={x}
      y={y}
      width={effectiveWidth}
      height={height}
      fill={fill}
      rx={0}
      className="cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
}
